import { useRecoilValue } from 'recoil'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled, { keyframes } from 'styled-components'

import { ISubData } from '@/util/types'
import { subDataState } from '@/util/states'
import { urlParser, addSizeToUrl } from '@/util/imgHelpers'

import { useApiCall } from 'src/hooks/useApiCall'
import { useGetCensored } from 'src/hooks/useGetCensored'
import { useGetPosts } from 'src/hooks/posts/useGetPosts'
import { Dashboard } from '../components/Dashboard/Dashboard'

export default function Home() {
  const subData = useRecoilValue(subDataState)

  if (subData === 'pending') return null

  const { isSubbed } = subData

  return isSubbed ? <Dashboard /> : <PublicHomepage subData={subData} />
}

const PublicHomepage = ({ subData }: { subData: ISubData }) => {
  const router = useRouter()
  
  // Fetch blog posts (editorials)
  const blogData = useApiCall({
    contentType: 'blogPost',
    order: '-fields.datePublished',
    limit: 4,
    options: { subscriptionRequired: false }
  })

  // Fetch censored drops for teaser
  const censoredDrops = useGetCensored({ limit: 4 })

  // Fetch discover posts (creator content)
  const { posts: discoverPosts } = useGetPosts({
    userId: null,
    orderBy: 'approvedAt',
    limit: 4,
    enablePagination: false,
  })

  const scrollToContent = () => {
    document.getElementById('content-start')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      <Head>
        <title>Beyond The Pines — Visual Poetry</title>
        <meta
          name="description"
          content="Beyond The Pines Magazine: breathtaking photography and visual provocation that stimulates thoughts, ideas, & conversations."
        />
        <meta
          name="keywords"
          content="Beyond The Pines, Beyond The Pines Magazine, photography, visual art, magazine, BTP"
        />
      </Head>

      <PageWrapper>
        {/* Hero Section */}
        <HeroSection>
          <HeroVideo autoPlay muted loop playsInline>
            <source src="https://videos.ctfassets.net/rxdl0mk5gigv/4WqAJO8qTHrzpI5LzQel7t/2e8ede1acd027fdeea8f9e453f3d8a74/about-video.mp4" type="video/mp4" />
          </HeroVideo>
          <HeroOverlay />
          <HeroContent>
            <HeroTagline>Beyond The Pines</HeroTagline>
            <HeroTitle>
              Where Vision<br />
              <GradientText>Becomes Poetry</GradientText>
            </HeroTitle>
            <HeroDescription>
              Breathtaking photography and visual provocation that stimulates thoughts, ideas, & conversations.
            </HeroDescription>
            <HeroButtonGroup>
              <PrimaryButton onClick={scrollToContent}>
                Explore Now
              </PrimaryButton>
              <SecondaryButton onClick={() => router.push('/subscribe')}>
                Join The Experience
              </SecondaryButton>
            </HeroButtonGroup>
          </HeroContent>
          <ScrollIndicator onClick={scrollToContent}>
            <ScrollText>Scroll to explore</ScrollText>
            <ScrollArrow>↓</ScrollArrow>
          </ScrollIndicator>
        </HeroSection>

        {/* Latest Editorials Section */}
        <Section id="content-start">
          <SectionHeader>
            <SectionTagline>Fresh Perspectives</SectionTagline>
            <SectionTitle>Latest Editorials</SectionTitle>
            <SectionSubtitle>
              Stories, interviews, and visual narratives from our world.
            </SectionSubtitle>
          </SectionHeader>

          {blogData?.length > 0 && (
            <ContentGrid columns={4}>
              {blogData.map((blog: any, index: number) => (
                <EditorialCard 
                  key={blog.id || index} 
                  onClick={() => router.push(`/blogs/${blog.blogUrl}`)}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardImageWrapper aspect="16/10">
                    <CardImage 
                      src={urlParser(blog.thumbnail, { width: 600, quality: 80, format: 'webp' })} 
                      alt={blog.title}
                    />
                    <CardOverlay />
                    <CardType>{blog.blogType}</CardType>
                  </CardImageWrapper>
                  <CardContent>
                    <CardTitle>{blog.title}</CardTitle>
                    <CardMeta>
                      <span>{blog.author}</span>
                      <span>·</span>
                      <span>{blog.datePublished}</span>
                    </CardMeta>
                  </CardContent>
                </EditorialCard>
              ))}
            </ContentGrid>
          )}

          <SectionCTA>
            <CTALink onClick={() => router.push('/blogs')}>
              View All Editorials →
            </CTALink>
          </SectionCTA>
        </Section>

        {/* Discover Section - Creator Content */}
        <Section dark>
          <SectionHeader>
            <SectionTagline>Creator Spotlight</SectionTagline>
            <SectionTitle>Discover</SectionTitle>
            <SectionSubtitle>
              Exclusive visual content from our community of creators.
            </SectionSubtitle>
          </SectionHeader>

          {discoverPosts?.length > 0 && (
            <ContentGrid columns={4}>
              {discoverPosts.map((post: any, index: number) => (
                <DiscoverCard 
                  key={post.id || index}
                  onClick={() => router.push(`/discover/${post.id}`)}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardImageWrapper aspect="4/5">
                    <CardImage 
                      src={addSizeToUrl(post.coverImg, post.coverImgTitle)} 
                      alt={post.title}
                    />
                    <CardOverlay />
                    <CreatorBadge>
                      @{post.authorUsername}
                    </CreatorBadge>
                  </CardImageWrapper>
                  <CardContent>
                    <CardTitle>{post.title}</CardTitle>
                    <CardDescription>
                      {post.description?.length > 60 
                        ? `${post.description.slice(0, 60)}…` 
                        : post.description}
                    </CardDescription>
                  </CardContent>
                </DiscoverCard>
              ))}
            </ContentGrid>
          )}

          <SectionCTA>
            <CTALink onClick={() => router.push('/discover')}>
              Explore All Creators →
            </CTALink>
          </SectionCTA>
        </Section>

        {/* Exclusive Drops Section - Teaser */}
        <Section>
          <SectionHeader>
            <SectionTagline>Members Only</SectionTagline>
            <SectionTitle>Exclusive Drops</SectionTitle>
            <SectionSubtitle>
              Unlock access to curated visual collections and behind-the-scenes content.
            </SectionSubtitle>
          </SectionHeader>

          {censoredDrops?.length > 0 && (
            <ContentGrid columns={4}>
              {censoredDrops.map((drop: any, index: number) => (
                <DropCard 
                  key={index}
                  onClick={() => router.push('/subscribe')}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardImageWrapper aspect="3/4">
                    <CardImage 
                      src={urlParser(drop.image, { width: 500, quality: 80, format: 'webp' })} 
                      alt={drop.title}
                    />
                    <LockedOverlay>
                      <LockIcon>🔒</LockIcon>
                      <LockText>Members Only</LockText>
                    </LockedOverlay>
                  </CardImageWrapper>
                  <CardContent>
                    <CardTitle>{drop.title}</CardTitle>
                  </CardContent>
                </DropCard>
              ))}
            </ContentGrid>
          )}

          <SectionCTA>
            <PrimaryButton onClick={() => router.push('/subscribe')}>
              Become a Member
            </PrimaryButton>
          </SectionCTA>
        </Section>

        {/* Impact Section */}
        <ImpactSection>
          <ImpactContent>
            <SectionTagline>Our Impact</SectionTagline>
            <ImpactTitle>Subscribe & Give Back</ImpactTitle>
            <ImpactSubtitle>
              Every membership makes a real difference. Together, we&apos;re building a better world.
            </ImpactSubtitle>
            
            <ImpactGrid>
              <ImpactCard>
                <ImpactIcon src="/images/btp-leaf.png" alt="Trees" />
                <ImpactNumber>22,000+</ImpactNumber>
                <ImpactLabel>Trees Planted</ImpactLabel>
              </ImpactCard>
              <ImpactCard>
                <ImpactIcon src="/images/btp-school.png" alt="Schools" />
                <ImpactNumber>1</ImpactNumber>
                <ImpactLabel>School Built</ImpactLabel>
              </ImpactCard>
            </ImpactGrid>
            <SecondaryButton style={{ marginTop: '40px' }} onClick={() => router.push('/about')}>
              Learn More
            </SecondaryButton>
          </ImpactContent>
        </ImpactSection>

        {/* Final CTA Section */}
        <FinalCTASection>
          <FinalCTAVideo autoPlay muted loop playsInline>
            <source src="/landingPage.mp4" type="video/mp4" />
          </FinalCTAVideo>
          <FinalCTAOverlay />
          <FinalCTAContent>
            <FinalCTATagline>Ready to dive in?</FinalCTATagline>
            <FinalCTATitle>Join Beyond The Pines</FinalCTATitle>
            <FinalCTASubtitle>
              A digital experience by Badboi. Best enjoyed by someone just like you.
            </FinalCTASubtitle>
            <PrimaryButton large onClick={() => router.push('/subscribe')}>
              Start Your Journey
            </PrimaryButton>
          </FinalCTAContent>
        </FinalCTASection>
      </PageWrapper>
    </>
  )
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
`

// Base Styled Components
const PageWrapper = styled.div`
  background: #0a0a0a;
  min-height: 100vh;
  overflow-x: hidden;
`

// Hero Section
const HeroSection = styled.section`
  position: relative;
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 24px 40px;
  background: #0a0a0a;
  overflow: hidden;

  @media (max-width: 600px) {
    min-height: 75vh;
    padding: 40px 20px 30px;
  }
`

const HeroVideo = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  z-index: 0;
`

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 10, 0.6) 0%,
    rgba(10, 10, 10, 0.4) 50%,
    rgba(10, 10, 10, 0.85) 100%
  );
  z-index: 1;
`

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  animation: ${fadeIn} 1s ease-out;
`

const HeroTagline = styled.span`
  display: inline-block;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #6B8E23;
  margin-bottom: 20px;
  padding: 8px 20px;
  border: 1px solid rgba(107, 142, 35, 0.4);
  border-radius: 30px;
  background: rgba(107, 142, 35, 0.1);
`

const HeroTitle = styled.h1`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(38px, 8vw, 80px);
  font-weight: 400;
  color: white;
  line-height: 1.05;
  margin: 0 0 20px;
  letter-spacing: -0.03em;
`

const GradientText = styled.span`
  -webkit-text-stroke: 2px rgba(255, 255, 255, 0.85);
  -webkit-text-fill-color: transparent;
  color: transparent;
`

const HeroDescription = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 17px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0 0 32px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 600px) {
    font-size: 15px;
    margin-bottom: 28px;
  }
`

const HeroButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;

  @media (min-width: 500px) {
    flex-direction: row;
    justify-content: center;
  }
`

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  text-align: center;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  @media (max-width: 600px) {
    bottom: 16px;
  }
`

const ScrollText = styled.span`
  display: block;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: white;
  margin-bottom: 8px;
`

const ScrollArrow = styled.span`
  display: block;
  font-size: 18px;
  color: white;
  animation: ${bounce} 2s ease-in-out infinite;
`

// Buttons
const PrimaryButton = styled.button<{ large?: boolean }>`
  background: linear-gradient(135deg, #556B2F 0%, #6B8E23 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: ${props => props.large ? '15px' : '13px'};
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: ${props => props.large ? '14px 44px' : '14px 32px'};
  cursor: pointer;
  transition: all 0.3s ease;
  width: auto;
  max-width: 280px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(85, 107, 47, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`

const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 13px;
  letter-spacing: 1px;
  padding: 14px 32px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.05);
  }
`

// Sections
const Section = styled.section<{ dark?: boolean }>`
  padding: 56px 24px;
  background: ${props => props.dark ? '#111' : '#0a0a0a'};

  @media (min-width: 800px) {
    padding: 72px 48px;
  }
`

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
  animation: ${fadeIn} 0.6s ease-out;
`

const SectionTagline = styled.span`
  display: inline-block;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #6B8E23;
  margin-bottom: 16px;
`

const SectionTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(28px, 5vw, 44px);
  color: white;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
`

const SectionSubtitle = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  max-width: 480px;
  margin: 0 auto;
  line-height: 1.6;
`

const SectionCTA = styled.div`
  text-align: center;
  margin-top: 48px;
`

const CTALink = styled.span`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 14px;
  color: #6B8E23;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.5px;

  &:hover {
    color: #8FBC3A;
  }
`

// Content Grid
const ContentGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 1300px;
  margin: 0 auto;

  @media (min-width: 700px) {
    grid-template-columns: repeat(${props => Math.min(props.columns || 4, 3)}, 1fr);
  }

  @media (min-width: 1000px) {
    grid-template-columns: repeat(${props => props.columns || 4}, 1fr);
    gap: 24px;
  }
`

// Cards
const BaseCard = styled.div`
  cursor: pointer;
  animation: ${fadeIn} 0.5s ease-out both;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-6px);
  }
`

const EditorialCard = styled(BaseCard)``

const DiscoverCard = styled(BaseCard)``

const DropCard = styled(BaseCard)``

const CardImageWrapper = styled.div<{ aspect?: string }>`
  position: relative;
  aspect-ratio: ${props => props.aspect || '3/4'};
  border-radius: 12px;
  overflow: hidden;
  background: #151515;
`

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${BaseCard}:hover & {
    transform: scale(1.05);
  }
`

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
`

const CardContent = styled.div`
  padding: 16px 4px 8px;
`

const CardTitle = styled.h3`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 15px;
  color: white;
  margin: 0 0 6px;
  line-height: 1.4;

  @media (min-width: 800px) {
    font-size: 16px;
  }
`

const CardDescription = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.5;
`

const CardMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`

const CardType = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 6px;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 10px;
  color: white;
  letter-spacing: 1px;
  text-transform: uppercase;
`

const CreatorBadge = styled.span`
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 6px;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  color: #6B8E23;
  letter-spacing: 0.5px;
`

const LockedOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: background 0.3s ease;

  ${DropCard}:hover & {
    background: rgba(0, 0, 0, 0.7);
  }
`

const LockIcon = styled.span`
  font-size: 32px;
  animation: ${float} 3s ease-in-out infinite;
`

const LockText = styled.span`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  color: white;
  letter-spacing: 2px;
  text-transform: uppercase;
`

// Impact Section
const ImpactSection = styled.section`
  padding: 56px 24px;
  background: linear-gradient(180deg, #0a0a0a 0%, #111 50%, #0a0a0a 100%);

  @media (min-width: 800px) {
    padding: 72px 48px;
  }
`

const ImpactContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`

const ImpactTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(28px, 5vw, 42px);
  color: white;
  margin: 0 0 16px;
`

const ImpactSubtitle = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0 0 50px;
`

const ImpactGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 80px;
  flex-wrap: wrap;

  @media (max-width: 500px) {
    gap: 50px;
  }
`

const ImpactCard = styled.div`
  text-align: center;
`

const ImpactIcon = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  margin: 0 auto 16px;
  display: block;
  filter: invert(1) brightness(0.3);
`

const ImpactNumber = styled.div`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 52px;
  color: #6B8E23;
  line-height: 1;
  margin-bottom: 8px;

  @media (max-width: 500px) {
    font-size: 40px;
  }
`

const ImpactLabel = styled.div`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1.5px;
`

// Final CTA Section
const FinalCTASection = styled.section`
  position: relative;
  padding: 100px 24px;
  background: #0a0a0a;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`

const FinalCTAVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const FinalCTAOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 10, 0.5) 0%,
    rgba(10, 10, 10, 0.4) 50%,
    rgba(10, 10, 10, 0.7) 100%
  );
`

const FinalCTAContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  animation: ${fadeIn} 0.8s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const FinalCTATagline = styled.span`
  display: inline-block;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
`

const FinalCTATitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(36px, 7vw, 56px);
  color: white;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
`

const FinalCTASubtitle = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 17px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 40px;
  line-height: 1.6;
`
